// src/components/Cart/CouponSection.jsx
import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
import { orderService } from '../../services/orderService';

const CouponSection = () => {
  const { cartData, applyCouponCode, removeCouponCode } = useCart();
  const { isLoggedIn } = useUser();
  const [coupons, setCoupons] = useState([]);
  const [inputCode, setInputCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (isLoggedIn()) {
      fetchCoupons();
    }
  }, [isLoggedIn]);

  const fetchCoupons = async () => {
    const data = await orderService.getActiveCoupons();
    setCoupons(data || []);
  };

  const handleApply = async (code) => {
    if (!isLoggedIn()) {
      setErrorMsg("Please login to apply coupons!");
      return;
    }
    if (!code.trim()) return;
    setLoading(true);
    setErrorMsg('');
    const res = await applyCouponCode(code.trim().toUpperCase());
    if (!res.success) {
      setErrorMsg(res.message);
    } else {
      setInputCode('');
    }
    setLoading(false);
  };

  const handleRemove = async () => {
    setLoading(true);
    await removeCouponCode();
    setLoading(false);
  };

  // If a coupon is already applied
  if (cartData?.couponCode) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
            <i className="fas fa-check-circle text-lg"></i>
          </div>
          <div>
            <p className="text-emerald-700 font-black text-sm uppercase tracking-wide">
              {cartData.couponCode} APPLIED
            </p>
            <p className="text-emerald-600 text-xs font-semibold">
              You saved ₹{Math.round(cartData.discountAmt || 0)} on this order!
            </p>
          </div>
        </div>
        <button
           onClick={handleRemove}
          disabled={loading}
          className="text-red-500 hover:text-red-700 font-bold text-xs uppercase tracking-widest px-2 transition-colors disabled:opacity-50"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      {/* Input Field Area */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <i className="fas fa-ticket-alt absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
             type="text"
             placeholder="Enter Coupon Code"
            value={inputCode}
            onChange={(e) => { setInputCode(e.target.value); setErrorMsg(''); }}
            className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2F63]/20 focus:border-[#0F2F63] text-sm font-bold uppercase transition-all"
          />
        </div>
        <button
           onClick={() => handleApply(inputCode)}
          disabled={loading || !inputCode.trim()}
          className="bg-[#0F2F63] hover:bg-[#1a4a8a] text-white px-6 py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50 shadow-md"
        >
          {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Apply'}
        </button>
      </div>

      {errorMsg && <p className="text-red-500 text-xs font-bold pl-2">{errorMsg}</p>}

      {/* Available Coupons Direct List */}
      {isLoggedIn() && coupons.length > 0 && (
        <div className="space-y-2 mt-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Available Offers</p>
          <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            {coupons.map((coupon) => {
              const isExpired = coupon.endDateTime ? new Date(coupon.endDateTime) < new Date() : false;
              
              return (
                <div key={coupon.id} className={`border border-dashed border-gray-200 rounded-xl p-3 relative overflow-hidden flex justify-between items-center gap-3 ${isExpired ? 'bg-gray-100 opacity-60' : 'bg-gray-50/50'}`}>
                  <div className={`absolute top-0 left-0 w-1 h-full ${isExpired ? 'bg-gray-400' : 'bg-[#FF7A59]'}`}></div>
                  
                  <div className="pl-2 flex-1">
                    <span className={`text-xs font-black uppercase tracking-wider ${isExpired ? 'text-gray-500' : 'text-[#FF7A59]'}`}>{coupon.couponCode}</span>
                    <p className="text-xs text-gray-600 font-medium line-clamp-1">{coupon.description}</p>
                    
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {coupon.minOrderAmt ? (
                        <span className="text-[10px] font-bold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-md shadow-sm">
                          Min Order: ₹{coupon.minOrderAmt}
                        </span>
                      ) : null}
                      
                      {coupon.endDateTime ? (
                        <span className={`text-[10px] font-bold border px-2 py-0.5 rounded-md flex items-center gap-1 shadow-sm ${isExpired ? 'text-gray-500 bg-gray-100 border-gray-200' : 'text-red-500 bg-red-50 border-red-100'}`}>
                          <i className="far fa-clock"></i> 
                          Expires: {new Date(coupon.endDateTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <button
                     onClick={() => handleApply(coupon.couponCode)}
                    disabled={loading || isExpired}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex-shrink-0 ${isExpired ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-sky-50 text-sky-600 hover:bg-sky-100'}`}
                  >
                    {isExpired ? 'Expired' : 'Apply'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponSection;